import type { Card, ChatEntry, ClientMessage, CompletedTrick, Emote, RoomSnapshot, Seat } from '@euchre/core';
import type { ConnStatus } from './net.js';
import { type DealerTalk, storedDealerTalk } from './ui/voice.js';

export interface FloatingEmote {
  id: number;
  seat: Seat | null;
  name: string;
  emote: Emote;
  until: number;
}

/** True on the preview site (preview.daveseuchre.com), where new designs are tried before release. */
export const IS_PREVIEW = /^preview[.-]/.test(location.hostname);

/** Who the host wants to play with: only bots (deal straight away), friends plus bots, or friends only. */
export type TableMode = 'bots' | 'friends-bots' | 'friends';

export interface AppState {
  conn: ConnStatus;
  playerId: string;
  name: string;
  room: RoomSnapshot | null;
  chat: ChatEntry[];
  unreadChat: number;
  toast: { text: string; until: number } | null;
  emotes: FloatingEmote[];
  /** Server time minus local time, for countdowns. */
  clockOffset: number;
  /** A just-completed trick kept on the table for a moment so everyone can see it. */
  heldTrick: { trick: CompletedTrick; until: number } | null;
  lastSeenTrickKey: string;
  /** The Vegas dealer's latest heckle and the deal it was about (shown on that hand's result card). */
  lastHeckle: { text: string; deal: number } | null;
  /** Room code from a share link, joined once we're connected. */
  pendingJoin: string | null;
  ui: {
    alone: boolean;
    farmerPick: Card[];
    showLastTrick: boolean;
    tab: 'chat' | 'log' | 'scores';
    rulesOpen: boolean;
    /** Table screen: the chat / hand log / score sheet panel slides in when open. Starts hidden. */
    drawerOpen: boolean;
    /** Table screen: the small menu with Leave seat / Leave table. */
    menuOpen: boolean;
    /** How the host said they want to play when starting this table (shapes the lobby's hints). */
    tableMode: TableMode | null;
    /** Lobby: the full house-rules list is shown (otherwise a one-line summary). */
    lobbyRulesOpen: boolean;
    /** This player's dealer voice: plain, or a wisecracking Vegas dealer. Saved on this device. */
    dealerTalk: DealerTalk;
  };
}

export const app: AppState = {
  conn: 'connecting',
  playerId: '',
  name: '',
  room: null,
  chat: [],
  unreadChat: 0,
  toast: null,
  emotes: [],
  clockOffset: 0,
  heldTrick: null,
  lastSeenTrickKey: '',
  pendingJoin: null,
  lastHeckle: null,
  ui: { alone: false, farmerPick: [], showLastTrick: false, tab: 'chat', rulesOpen: false, drawerOpen: false, menuOpen: false, tableMode: null, lobbyRulesOpen: false, dealerTalk: storedDealerTalk() },
};

let sender: (msg: ClientMessage) => void = () => {};
let renderer: () => void = () => {};

export function wire(send: (msg: ClientMessage) => void, render: () => void): void {
  sender = send;
  renderer = render;
}

export function send(msg: ClientMessage): void {
  sender(msg);
}

let scheduled = false;
export function rerender(): void {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    renderer();
  });
}

/** Re-render at a future time (for timed UI like held tricks and countdowns). */
export function rerenderAt(ms: number): void {
  window.setTimeout(rerender, Math.max(0, ms - Date.now()) + 20);
}

export function toast(text: string, ms = 3500): void {
  app.toast = { text, until: Date.now() + ms };
  rerender();
  rerenderAt(app.toast.until);
}

export function serverNow(): number {
  return Date.now() + app.clockOffset;
}
